/**
 * Queries — raycast / sphereCast / capsule sweep / collide-and-slide.
 *
 * All of these run against a TriBVH. Nothing here allocates in a steady frame:
 * results are written into pooled objects that the caller may copy from.
 *
 * Capsule convention (matches CONTRACTS.md):
 *   `pos`        is the capsule CENTRE
 *   `halfHeight` is the half-length of the inner SEGMENT (not including the caps)
 *   total height = 2 * (halfHeight + radius)
 *   e.g. a 1.8 m player of radius 0.32 -> halfHeight = 0.58
 *
 * owner: physics
 */
import * as THREE from 'three';
import { segTriDistance, sweepCapsuleTriangle, closestPointOnTriangle } from './PhysMath.js';

const SKIN = 0.004;          // keep this much clearance from surfaces
const DEPEN_EPS = 1e-4;

const _hit = { t: 0, tri: -1, nx: 0, ny: 0, nz: 0, px: 0, py: 0, pz: 0, depth: 0 };
const _sw = { t: 0, nx: 0, ny: 0, nz: 0, px: 0, py: 0, pz: 0, depth: 0 };
const _segP = { x: 0, y: 0, z: 0 };
const _triP = { x: 0, y: 0, z: 0 };
const _cp = { x: 0, y: 0, z: 0 };

export class Queries {
  constructor(bvh) {
    this.bvh = bvh;
    this._cand = new Int32Array(8192);
    this._allSink = new Array(128);
    this.stats = { rays: 0, sweeps: 0, triTests: 0 };
    // debug taps — the debug renderer reads these
    this.debugContacts = [];
    this.debugCapsules = [];
    this.recordDebug = false;
  }

  setBVH(bvh) { this.bvh = bvh; }

  /** Copy BVH box-query candidates into our own buffer (the BVH buffer is shared). */
  _gather(mnx, mny, mnz, mxx, mxy, mxz, mask) {
    const n = this.bvh.queryBox(mnx, mny, mnz, mxx, mxy, mxz, mask);
    if (n > this._cand.length) this._cand = new Int32Array(n + 512);
    this._cand.set(this.bvh._cand.subarray(0, n));
    return n;
  }

  /* ------------------------------------------------------------------ *
   * Ray queries
   * ------------------------------------------------------------------ */

  /** @returns {{point,normal,distance,object,surface,triangle}|null} */
  raycast(origin, dir, far = 500, mask = 0xffff, out = null) {
    this.stats.rays++;
    const d = _tmpDir.copy(dir);
    const len = d.length();
    if (len < 1e-9) return null;
    d.multiplyScalar(1 / len);
    if (!this.bvh.raycast(origin.x, origin.y, origin.z, d.x, d.y, d.z, far, mask, _hit)) return null;
    const r = out || makeRayResult();
    r.point.set(_hit.px, _hit.py, _hit.pz);
    r.normal.set(_hit.nx, _hit.ny, _hit.nz);
    r.distance = _hit.t;
    r.triangle = _hit.tri;
    r.object = this.bvh.objectOf(_hit.tri);
    r.surface = this.bvh.surfaceOf(_hit.tri);
    r.body = null;
    return r;
  }

  /** All hits, sorted near→far. Used by weapons for wall penetration. */
  raycastAll(origin, dir, far = 500, mask = 0xffff) {
    const d = _tmpDir.copy(dir);
    const len = d.length();
    if (len < 1e-9) return [];
    d.multiplyScalar(1 / len);
    const n = this.bvh.raycastAll(origin.x, origin.y, origin.z, d.x, d.y, d.z, far, mask, this._allSink);
    const arr = [];
    for (let i = 0; i < n; i++) arr.push(this._allSink[i]);
    arr.sort((a, b) => a.t - b.t);
    const out = [];
    for (let i = 0; i < arr.length; i++) {
      const { t, tri } = arr[i];
      const n3 = tri * 3;
      let nx = this.bvh.nrm[n3], ny = this.bvh.nrm[n3 + 1], nz = this.bvh.nrm[n3 + 2];
      if (nx * d.x + ny * d.y + nz * d.z > 0) { nx = -nx; ny = -ny; nz = -nz; }
      out.push({
        point: new THREE.Vector3(origin.x + d.x * t, origin.y + d.y * t, origin.z + d.z * t),
        normal: new THREE.Vector3(nx, ny, nz),
        distance: t,
        triangle: tri,
        object: this.bvh.objectOf(tri),
        surface: this.bvh.surfaceOf(tri),
        body: null,
      });
    }
    return out;
  }

  /** A sphere swept along dir. Degenerate capsule (halfHeight = 0). */
  sphereCast(origin, dir, radius, far = 100, mask = 0xffff) {
    const d = _tmpDir.copy(dir);
    const len = d.length();
    if (len < 1e-9) return null;
    d.multiplyScalar(1 / len);
    const delta = _tmpDelta.copy(d).multiplyScalar(far);
    const r = this.sweepCapsule(origin, origin, radius, delta, mask, _sw);
    if (!r) return null;
    return {
      point: new THREE.Vector3(_sw.px, _sw.py, _sw.pz),
      normal: new THREE.Vector3(_sw.nx, _sw.ny, _sw.nz),
      distance: _sw.t * far,
      fraction: _sw.t,
      depth: _sw.depth,
      surface: this.bvh.surfaceOf(_sw.tri ?? 0),
      triangle: _sw.tri,
      object: _sw.tri >= 0 ? this.bvh.objectOf(_sw.tri) : null,
      body: null,
    };
  }

  /* ------------------------------------------------------------------ *
   * Capsule sweep
   * ------------------------------------------------------------------ */

  /**
   * Sweep the capsule segment [p0,p1] (world) with `radius` along `delta`.
   * Writes into `out` and returns true on hit.  out.t is a fraction of delta.
   */
  sweepCapsule(p0, p1, radius, delta, mask, out) {
    this.stats.sweeps++;
    const bvh = this.bvh;
    if (bvh.triCount === 0) return false;

    const pad = radius + SKIN + 1e-3;
    const mnx = Math.min(p0.x, p1.x, p0.x + delta.x, p1.x + delta.x) - pad;
    const mny = Math.min(p0.y, p1.y, p0.y + delta.y, p1.y + delta.y) - pad;
    const mnz = Math.min(p0.z, p1.z, p0.z + delta.z, p1.z + delta.z) - pad;
    const mxx = Math.max(p0.x, p1.x, p0.x + delta.x, p1.x + delta.x) + pad;
    const mxy = Math.max(p0.y, p1.y, p0.y + delta.y, p1.y + delta.y) + pad;
    const mxz = Math.max(p0.z, p1.z, p0.z + delta.z, p1.z + delta.z) + pad;

    const n = this._gather(mnx, mny, mnz, mxx, mxy, mxz, mask);
    if (n === 0) return false;

    const cand = this._cand, P = bvh.pos;
    let bestT = Infinity, bestTri = -1;
    let bnx = 0, bny = 0, bnz = 0, bpx = 0, bpy = 0, bpz = 0, bdepth = 0;
    this.stats.triTests += n;

    for (let i = 0; i < n; i++) {
      const tri = cand[i];
      const o = tri * 9;
      if (!sweepCapsuleTriangle(
        p0.x, p0.y, p0.z, p1.x, p1.y, p1.z, radius,
        delta.x, delta.y, delta.z,
        P[o], P[o + 1], P[o + 2], P[o + 3], P[o + 4], P[o + 5], P[o + 6], P[o + 7], P[o + 8],
        1, _sweepOut,
      )) continue;
      if (_sweepOut.t < bestT) {
        bestT = _sweepOut.t; bestTri = tri;
        bnx = _sweepOut.nx; bny = _sweepOut.ny; bnz = _sweepOut.nz;
        bpx = _sweepOut.px; bpy = _sweepOut.py; bpz = _sweepOut.pz;
        bdepth = _sweepOut.depth;
        if (bestT <= 0) break;
      }
    }
    if (bestTri < 0) return false;
    out.t = bestT; out.tri = bestTri;
    out.nx = bnx; out.ny = bny; out.nz = bnz;
    out.px = bpx; out.py = bpy; out.pz = bpz;
    out.depth = bdepth;
    return true;
  }

  /* ------------------------------------------------------------------ *
   * Overlap / depenetration
   * ------------------------------------------------------------------ */

  /** True if the capsule at `centre` overlaps any static triangle. */
  overlapCapsule(centre, halfHeight, radius, mask = 0xffff, tolerance = 0) {
    const bvh = this.bvh;
    if (bvh.triCount === 0) return false;
    const pad = radius + 1e-4;
    const y0 = centre.y - halfHeight, y1 = centre.y + halfHeight;
    const n = this._gather(
      centre.x - pad, y0 - pad, centre.z - pad,
      centre.x + pad, y1 + pad, centre.z + pad, mask,
    );
    const cand = this._cand, P = bvh.pos;
    const limit = radius - tolerance;
    for (let i = 0; i < n; i++) {
      const o = cand[i] * 9;
      const d = segTriDistance(
        centre.x, y0, centre.z, centre.x, y1, centre.z,
        P[o], P[o + 1], P[o + 2], P[o + 3], P[o + 4], P[o + 5], P[o + 6], P[o + 7], P[o + 8],
        _segP, _triP,
      );
      if (d < limit) return true;
    }
    return false;
  }

  /**
   * Push a capsule out of any geometry it is inside. Mutates `centre`.
   * Returns the total correction magnitude applied.
   */
  depenetrate(centre, halfHeight, radius, mask = 0xffff, iterations = 4) {
    const bvh = this.bvh;
    if (bvh.triCount === 0) return 0;
    let total = 0;
    const target = radius + SKIN;
    for (let it = 0; it < iterations; it++) {
      const pad = target + 1e-3;
      const y0 = centre.y - halfHeight, y1 = centre.y + halfHeight;
      const n = this._gather(
        centre.x - pad, y0 - pad, centre.z - pad,
        centre.x + pad, y1 + pad, centre.z + pad, mask,
      );
      if (n === 0) break;
      const cand = this._cand, P = bvh.pos;
      let cx = 0, cy = 0, cz = 0, worst = 0;
      for (let i = 0; i < n; i++) {
        const tri = cand[i];
        const o = tri * 9;
        const d = segTriDistance(
          centre.x, y0, centre.z, centre.x, y1, centre.z,
          P[o], P[o + 1], P[o + 2], P[o + 3], P[o + 4], P[o + 5], P[o + 6], P[o + 7], P[o + 8],
          _segP, _triP,
        );
        if (d >= target) continue;
        const push = target - d;
        let nx, ny, nz;
        if (d > 1e-6) { nx = (_segP.x - _triP.x) / d; ny = (_segP.y - _triP.y) / d; nz = (_segP.z - _triP.z) / d; }
        else { const n3 = tri * 3; nx = bvh.nrm[n3]; ny = bvh.nrm[n3 + 1]; nz = bvh.nrm[n3 + 2]; }
        // accumulate the *maximum* push per axis-direction rather than summing,
        // so a flat floor made of many triangles doesn't over-correct.
        const dot = cx * nx + cy * ny + cz * nz;
        if (dot < push) {
          const add = push - Math.max(0, dot);
          cx += nx * add; cy += ny * add; cz += nz * add;
        }
        if (push > worst) worst = push;
      }
      if (worst <= DEPEN_EPS) break;
      centre.x += cx; centre.y += cy; centre.z += cz;
      total += Math.sqrt(cx * cx + cy * cy + cz * cz);
    }
    return total;
  }

  /** True if the point is inside solid geometry (parity test along +Y). */
  pointInSolid(p, mask = 0xffff) {
    const bvh = this.bvh;
    if (bvh.triCount === 0) return false;
    if (p.y > bvh.bounds.max[1] || p.y < bvh.bounds.min[1]) return false;
    if (p.x > bvh.bounds.max[0] || p.x < bvh.bounds.min[0]) return false;
    if (p.z > bvh.bounds.max[2] || p.z < bvh.bounds.min[2]) return false;
    // Crossing parity upward. Level geometry is closed boxes, so this is exact
    // enough; ties are broken by a slightly skewed direction.
    const n = bvh.raycastAll(p.x, p.y, p.z, 0.0017, 0.99999, 0.0031, 1e4, mask, this._allSink);
    return (n & 1) === 1;
  }

  /* ------------------------------------------------------------------ *
   * capsuleMove — collide and slide with step-up and ground snapping
   * ------------------------------------------------------------------ */

  /**
   * @param {THREE.Vector3} pos    capsule CENTRE (not mutated)
   * @param {number} halfHeight    inner segment half-length
   * @param {number} radius
   * @param {THREE.Vector3} delta  desired displacement for this step
   * @param {object} [opts]
   *   mask         collision mask (default MASK.ALL)
   *   stepHeight   max obstacle height to step over, metres (default 0.45)
   *   slopeLimit   max walkable slope, radians (default 50°)
   *   snapToGround snap down on descent (default true)
   *   grounded     was the mover grounded last frame? gates step-up + snapping
   *   iterations   collide-and-slide passes (default 5)
   * @returns {{position, grounded, groundNormal, hitWall, steppedUp}}
   */
  capsuleMove(pos, halfHeight, radius, delta, opts = {}) {
    const mask = opts.mask ?? 0xffff;
    const stepHeight = opts.stepHeight ?? 0.45;
    const slopeLimit = opts.slopeLimit ?? (50 * Math.PI / 180);
    const minGroundY = Math.cos(slopeLimit);
    const iterations = opts.iterations ?? 5;
    const snap = opts.snapToGround !== false;
    const result = opts.out || makeMoveResult();

    const p = _mvPos.copy(pos);
    const rem = _mvRem.copy(delta);
    result.grounded = false;
    result.hitWall = false;
    result.steppedUp = 0;
    result.groundNormal.set(0, 1, 0);
    result.contacts = 0;

    if (this.bvh.triCount === 0) {
      result.position.copy(p).add(rem);
      return result;
    }

    // 0. never start inside geometry
    this.depenetrate(p, halfHeight, radius, mask, 3);

    // Was the mover standing on something? Gates step-up and ground snapping.
    let wasGrounded = opts.grounded;
    if (wasGrounded === undefined) {
      wasGrounded = this._probeGround(p, halfHeight, radius, mask, 0.06, minGroundY, _probe);
    }

    const planes = _planes;
    let planeCount = 0;

    for (let iter = 0; iter < iterations; iter++) {
      const remLen = rem.length();
      if (remLen < 1e-6) break;

      _p0.set(p.x, p.y - halfHeight, p.z);
      _p1.set(p.x, p.y + halfHeight, p.z);

      if (!this.sweepCapsule(_p0, _p1, radius, rem, mask, _sw)) {
        p.add(rem);
        rem.set(0, 0, 0);
        break;
      }

      // advance up to the contact, holding back the skin
      const travel = Math.max(0, _sw.t * remLen - SKIN);
      const f = travel / remLen;
      p.x += rem.x * f; p.y += rem.y * f; p.z += rem.z * f;
      rem.multiplyScalar(1 - Math.min(1, _sw.t));

      const nx = _sw.nx, ny = _sw.ny, nz = _sw.nz;
      result.contacts++;
      if (this.recordDebug) this.debugContacts.push(_sw.px, _sw.py, _sw.pz, nx, ny, nz);

      const walkable = ny >= minGroundY;
      if (walkable) {
        if (!result.grounded || ny > result.groundNormal.y) result.groundNormal.set(nx, ny, nz);
        result.grounded = true;
      } else if (ny > -0.2) {
        result.hitWall = true;
        // --- step up ---
        if (stepHeight > 0 && (wasGrounded || result.grounded)) {
          const stepped = this._tryStepUp(p, halfHeight, radius, rem, mask, stepHeight, minGroundY);
          if (stepped > 0) {
            result.steppedUp += stepped;
            result.grounded = true;
            result.groundNormal.copy(_stepNormal);
            planeCount = 0;
            continue;
          }
        }
      }

      // --- slide: project the remaining motion onto the contact plane ---
      if (planeCount < planes.length) planes[planeCount++].set(nx, ny, nz);
      this._clipMotion(rem, planes, planeCount);
      if (rem.lengthSq() < 1e-12) break;
    }

    // --- ground snapping on descent ---
    if (snap && !result.grounded && wasGrounded && delta.y <= 1e-4) {
      const maxSnap = Math.min(stepHeight, 0.5);
      if (this._probeGround(p, halfHeight, radius, mask, maxSnap, minGroundY, _probe)) {
        p.y -= Math.max(0, _probe.dist - SKIN);
        result.grounded = true;
        result.groundNormal.copy(_probe.normal);
      }
    }

    // --- final safety: never leave the capsule inside geometry ---
    this.depenetrate(p, halfHeight, radius, mask, 4);

    if (!result.grounded) {
      // one cheap check so callers get a correct `grounded` after depenetration
      if (this._probeGround(p, halfHeight, radius, mask, SKIN * 3, minGroundY, _probe)) {
        result.grounded = true;
        result.groundNormal.copy(_probe.normal);
      }
    }

    result.position.copy(p);
    return result;
  }

  /**
   * Try to step up over the obstacle blocking `rem`.
   * Returns the rise achieved (0 = failed) and leaves `p` moved on success.
   */
  _tryStepUp(p, halfHeight, radius, rem, mask, stepHeight, minGroundY) {
    const hx = rem.x, hz = rem.z;
    const hLen = Math.sqrt(hx * hx + hz * hz);
    if (hLen < 1e-5) return 0;
    const dirx = hx / hLen, dirz = hz / hLen;
    // push a little further than the remaining motion so we clear the lip
    const forward = Math.max(hLen, radius * 0.6);

    // 1) rise — with a ceiling check so we never step into something
    _stepP.copy(p);
    _p0.set(_stepP.x, _stepP.y - halfHeight, _stepP.z);
    _p1.set(_stepP.x, _stepP.y + halfHeight, _stepP.z);
    _tmpDelta.set(0, stepHeight, 0);
    let rise = stepHeight;
    if (this.sweepCapsule(_p0, _p1, radius, _tmpDelta, mask, _sw)) {
      rise = Math.max(0, _sw.t * stepHeight - SKIN);
      if (rise < 0.02) return 0;            // ceiling right above us
    }
    _stepP.y += rise;

    // 2) move forward at the raised height
    _p0.set(_stepP.x, _stepP.y - halfHeight, _stepP.z);
    _p1.set(_stepP.x, _stepP.y + halfHeight, _stepP.z);
    _tmpDelta.set(dirx * forward, 0, dirz * forward);
    let adv = forward;
    if (this.sweepCapsule(_p0, _p1, radius, _tmpDelta, mask, _sw)) {
      adv = Math.max(0, _sw.t * forward - SKIN);
    }
    if (adv < 1e-3) return 0;               // still blocked at the raised height
    _stepP.x += dirx * adv; _stepP.z += dirz * adv;

    // 3) drop back down onto the step
    const drop = rise + SKIN * 2;
    _p0.set(_stepP.x, _stepP.y - halfHeight, _stepP.z);
    _p1.set(_stepP.x, _stepP.y + halfHeight, _stepP.z);
    _tmpDelta.set(0, -drop, 0);
    if (!this.sweepCapsule(_p0, _p1, radius, _tmpDelta, mask, _sw)) return 0;
    if (_sw.ny < minGroundY) return 0;      // landed on something too steep
    const fall = Math.max(0, _sw.t * drop - SKIN);
    _stepP.y -= fall;

    const gained = _stepP.y - p.y;
    if (gained <= 1e-4) return 0;           // no actual step, ordinary slide is fine
    if (this.overlapCapsule(_stepP, halfHeight, radius, mask, SKIN * 0.5)) return 0;

    _stepNormal.set(_sw.nx, _sw.ny, _sw.nz);
    // consume the horizontal motion we used
    const used = Math.min(1, adv / hLen);
    rem.x -= dirx * hLen * used;
    rem.z -= dirz * hLen * used;
    if (rem.y < 0) rem.y = 0;
    p.copy(_stepP);
    return gained;
  }

  /**
   * Downward probe. Fills `out` with { dist, normal }.
   *
   * The capsule sweep alone is not enough: walking down a staircase, the
   * capsule's side clips the riser of the step below and the sweep reports a
   * vertical (non-walkable) normal, so the snap is refused and the mover
   * launches off every tread. When that happens we fall back to rays straight
   * down from under the capsule.
   */
  _probeGround(p, halfHeight, radius, mask, maxDist, minGroundY, out) {
    const reach = maxDist + SKIN;
    _p0.set(p.x, p.y - halfHeight, p.z);
    _p1.set(p.x, p.y + halfHeight, p.z);
    _tmpDelta.set(0, -reach, 0);
    if (this.sweepCapsule(_p0, _p1, radius, _tmpDelta, mask, _sw) && _sw.ny >= minGroundY) {
      out.dist = _sw.t * reach;
      out.normal.set(_sw.nx, _sw.ny, _sw.nz);
      return out.dist <= reach;
    }

    // fallback: 5 downward rays (centre + 4 at 60% radius)
    const bottom = halfHeight + radius;
    let best = Infinity;
    _probeDown.set(0, -1, 0);
    for (let i = 0; i < 5; i++) {
      const a = (i - 1) * Math.PI * 0.5;
      const ox = i === 0 ? 0 : Math.cos(a) * radius * 0.6;
      const oz = i === 0 ? 0 : Math.sin(a) * radius * 0.6;
      _probeOrigin.set(p.x + ox, p.y, p.z + oz);
      const h = this.raycast(_probeOrigin, _probeDown, bottom + reach, mask, _probeRay);
      if (!h) continue;
      if (h.normal.y < minGroundY) continue;
      // A capsule riding a stair nosing already touches ground slightly ABOVE
      // its own bottom, so allow a small negative drop — otherwise `grounded`
      // flickers off on every tread and the player team sees phantom air time.
      const drop = h.distance - bottom;
      if (drop < -0.08) continue;
      if (drop < best) { best = drop; out.normal.copy(h.normal); }
    }
    if (best === Infinity || best > maxDist) return false;
    // Never snap INTO something. If the drop would clip geometry (typical when
    // the capsule is straddling a stair nosing) we still report grounded — we
    // are demonstrably supported — we just do not move down.
    _probeTest.set(p.x, p.y - Math.max(0, best - SKIN), p.z);
    if (this.overlapCapsule(_probeTest, halfHeight, radius, mask, SKIN * 0.5)) best = 0;
    out.dist = Math.max(0, best);
    return true;
  }

  /**
   * Clip `v` so it does not move into any of the `count` planes.
   * Handles the two-plane crease case (Quake's classic ClipVelocity) so the
   * mover doesn't oscillate or stall in a corner.
   */
  _clipMotion(v, planes, count) {
    for (let i = 0; i < count; i++) {
      const n = planes[i];
      const d = v.dot(n);
      if (d >= 0) continue;
      v.addScaledVector(n, -d);
      // re-check earlier planes; if we now violate one, slide along the crease
      for (let j = 0; j < count; j++) {
        if (j === i) continue;
        const m = planes[j];
        if (v.dot(m) >= 0) continue;
        _crease.crossVectors(n, m);
        const len = _crease.length();
        if (len < 1e-6) { v.set(0, 0, 0); return; }
        _crease.multiplyScalar(1 / len);
        v.copy(_crease).multiplyScalar(_crease.dot(v));
        // a third plane means a wedge — stop
        for (let k = 0; k < count; k++) {
          if (k === i || k === j) continue;
          if (v.dot(planes[k]) < 0) { v.set(0, 0, 0); return; }
        }
        return;
      }
    }
  }
}

/* ---- pooled result objects ---- */
export function makeRayResult() {
  return {
    point: new THREE.Vector3(), normal: new THREE.Vector3(),
    distance: 0, object: null, surface: 'concrete', triangle: -1, body: null,
  };
}
export function makeMoveResult() {
  return {
    position: new THREE.Vector3(), grounded: false,
    groundNormal: new THREE.Vector3(0, 1, 0), hitWall: false, steppedUp: 0, contacts: 0,
  };
}

/* ---- module scratch ---- */
const _tmpDir = new THREE.Vector3();
const _tmpDelta = new THREE.Vector3();
const _mvPos = new THREE.Vector3();
const _mvRem = new THREE.Vector3();
const _p0 = new THREE.Vector3();
const _p1 = new THREE.Vector3();
const _stepP = new THREE.Vector3();
const _stepNormal = new THREE.Vector3(0, 1, 0);
const _crease = new THREE.Vector3();
const _probe = { dist: 0, normal: new THREE.Vector3(0, 1, 0) };
const _probeOrigin = new THREE.Vector3();
const _probeDown = new THREE.Vector3(0, -1, 0);
const _probeTest = new THREE.Vector3();
const _probeRay = makeRayResult();
const _planes = [
  new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3(),
  new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3(),
];
const _sweepOut = { t: 0, nx: 0, ny: 0, nz: 0, px: 0, py: 0, pz: 0, depth: 0 };

export { SKIN };
export { closestPointOnTriangle, _cp };
